import React, { useState, useEffect } from 'react';
import {
    Box, Button, Stack, IconButton, Tooltip
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { requestShowLoader, requestHideLoader } from '../../Store/Global/action';
import { StyledSubHeader } from '../../Components/Styled';
import LeadDialog from '../../Components/LeadDialog';

import {
    handleValidateCodevalue
} from '../../Utility/constant';
import {
    WORKFLOW_API_KEY_LOCATIONS,
    WORKFLOW_AUTH_TYPES,
    WORKFLOW_HTTP_METHODS,
    defaultApiAccessConfig,
    getWorkflowAuthTypesForProvider,
    getWorkflowProviderConfig,
    isMetaOAuthConfig,
    workflowApiKeyLocations,
    workflowHttpMethods,
    workflowThirdPartyProviders,
    validateWorkflowApiAccess
} from '../../Utility/workflowConfig';
import TextFiledWithLabel from '../../Components/TextFieldWithLabel';
import SelectBoxWithLabel from '../../Components/SelectBoxWithLable';
import { useSnackbar } from 'notistack';
import { createRequestData, getRequestData } from '../../Utility/apiHelper';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import tableBodyicon from '../../Assets/images/tablebody_edit.png';
import './workflow.css';

const InfoText = ({ children }) => (
    <span className="info-text mt-15 mb-20">
        <InfoOutlinedIcon className="workflow-info-icon" />
        {children}
    </span>
);

const ApiAccessDetailsDialog = ({ open, isViewMode, defaultValues, handleCloseDialog, onSave }) => {
    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [config, setConfig] = useState({ ...defaultApiAccessConfig, ...(defaultValues || {}) });

    // Meta integration state
    const [pages, setPages] = useState([]);             // returned by /api/meta/connect
    const [forms, setForms] = useState([]);             // returned by /api/meta/forms
    // Controls whether the token entry field is editable vs. showing the masked display
    const [isEditingToken, setIsEditingToken] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const providerConfig = getWorkflowProviderConfig(config.provider);
    const authTypeOptions = getWorkflowAuthTypesForProvider(config.provider);

    // Reset wizard when dialog opens/closes
    useEffect(() => {
        if (open) {
            const merged = { ...defaultApiAccessConfig, ...(defaultValues || {}) };
            setConfig(merged);
            setIsEditingToken(false);
            setValidationErrors({});
            if (merged.page_access_token) {
                // Rebuild page/form "options" from saved names so dropdowns show correctly
                if (merged.page_id) {
                    setPages([{ id: merged.page_id, name: merged.page_name || merged.page_id, access_token: merged.page_access_token }]);
                }
                if (merged.form_id) {
                    setForms([{ id: merged.form_id, name: merged.form_name || merged.form_id }]);
                }
            } else {
                setPages([]);
                setForms([]);
            }
        }
    }, [defaultValues, open]);

    const handleChange = (event) => {
        const { name, value } = event?.target || {};
        // Reset fields when provider/authType changes
        if (name === 'provider' || name === 'authType') {
            setPages([]);
            setForms([]);
            setValidationErrors({});
        }
        setConfig(prev => {
            const nextConfig = { ...prev, [name]: value };
            if (name === 'provider') {
                const nextAuthOptions = getWorkflowAuthTypesForProvider(value);
                nextConfig.authType = nextAuthOptions[0]?.value || "";
            }
            return nextConfig;
        });
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    // ── Meta Step 1: Connect ─────────────────────────────────────────────────
    const handleMetaConnect = async () => {
        if (!config.app_id || !config.app_secret || !config.short_lived_token) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            return;
        }
        dispatch(requestShowLoader());
        try {
            const url = `${process.env.REACT_APP_BASE_URL}api/meta/connect`;
            const response = await createRequestData(
                { app_id: config.app_id, app_secret: config.app_secret, short_lived_token: config.short_lived_token },
                url
            );
            if (response?.status === 200 && response?.data?.pages?.length > 0) {
                setPages(response.data.pages);
                setForms([]);
                setConfig(prev => ({ ...prev, page_id: "", page_name: "", page_access_token: "", form_id: "", form_name: "" }));
                setIsEditingToken(false);
                enqueueSnackbar(handleValidateCodevalue(internationalise, "WF_TP_META_CONNECT_SUCCESS") || "Connected to Meta successfully!", { variant: "success" });
            } else {
                const msg = response?.data?.message || response?.propererror?.data?.message || "WF_TP_META_CONNECT_FAIL";
                enqueueSnackbar(handleValidateCodevalue(internationalise, msg) || msg, { variant: "error" });
            }
        } catch (err) {
            const msg = err?.message || "WF_TP_META_CONNECT_FAIL";
            enqueueSnackbar(handleValidateCodevalue(internationalise, msg) || msg, { variant: "error" });
        } finally {
            dispatch(requestHideLoader());
        }
    };

    // ── Meta Step 2: Page selected → fetch forms ─────────────────────────────
    const handlePageSelect = async (event) => {
        const selectedPageId = event?.target?.value;
        const selectedPage = pages.find(p => p.id === selectedPageId);
        if (!selectedPage) return;

        setConfig(prev => ({
            ...prev,
            page_id: selectedPage.id,
            page_name: selectedPage.name,
            page_access_token: selectedPage.access_token,
            form_id: "",
            form_name: ""
        }));
        setForms([]);
        dispatch(requestShowLoader());
        setValidationErrors(prev => ({ ...prev, page_id: false, form_id: false }));

        try {
            const url = `${process.env.REACT_APP_BASE_URL}api/meta/forms?page_id=${selectedPage.id}&page_access_token=${encodeURIComponent(selectedPage.access_token)}`;
            const response = await getRequestData(url);
            if (response?.status === 200 && response?.data?.forms) {
                setForms(response.data.forms);
            } else {
                const msg = response?.data?.message || "WF_TP_META_FORM_FAIL";
                enqueueSnackbar(handleValidateCodevalue(internationalise, msg) || msg, { variant: "error" });
            }
        } catch (err) {
            const msg = err?.message || "WF_TP_META_FORM_FAIL";
            enqueueSnackbar(handleValidateCodevalue(internationalise, msg) || msg, { variant: "error" });
        } finally {
            dispatch(requestHideLoader());
        }
    };

    const handleFormSelect = (event) => {
        const selectedFormId = event?.target?.value;
        const selectedForm = forms.find(f => f.id === selectedFormId);
        if (!selectedForm) return;
        setConfig(prev => ({ ...prev, form_id: selectedForm.id, form_name: selectedForm.name }));
        setValidationErrors(prev => ({ ...prev, form_id: false }));
    };

    // ── Save validation ───────────────────────────────────────────────────────
    const handleSaveClick = () => {
        const { newErrors, hasError } = validateWorkflowApiAccess(config);

        setValidationErrors(newErrors);

        if (hasError) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS"), { variant: "error" });
            return;
        }
        onSave(config);
    };

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderMetaWizard = () => (
        <Stack className="workflow-api-form-stack">
            <StyledSubHeader>Step 1 : Meta App Credentials</StyledSubHeader>
            <InfoText>Enter your Meta app details and access token, then connect to fetch available pages.</InfoText>

            <Stack className="workflow-api-form-row">
                <TextFiledWithLabel
                    name="app_id"
                    required
                    disabled={isViewMode}
                    value={config.app_id || ''}
                    label="Meta App ID"
                    handleOnChange={handleChange}
                    className="workflow-api-field-48"
                />
                <TextFiledWithLabel
                    name="app_secret"
                    required
                    disabled={isViewMode}
                    value={config.app_secret || ''}
                    label="Meta App Secret"
                    handleOnChange={handleChange}
                    className="workflow-api-field-48"
                />
            </Stack>

            {config.page_access_token && !isEditingToken ? (
                <Box className="workflow-api-token-row">
                    <TextFiledWithLabel
                        name="long_lived_token_display"
                        disabled={true}
                        value={config.page_access_token || ''}
                        label="Long-lived Access Token"
                        className="workflow-api-token"
                    />
                    <Tooltip title="Enter token to reconnect">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setIsEditingToken(true)}
                                disabled={isViewMode}
                            >
                                <Tooltip title={handleValidateCodevalue(internationalise,'COMMON_EDIT')} >
                                    <img src={tableBodyicon} className="workflow-api-edit-icon" />
                                </Tooltip>,
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ) : (
                <>
                    <TextFiledWithLabel
                        name="short_lived_token"
                        required
                        disabled={isViewMode}
                        value={config.short_lived_token || ''}
                        label="Access Token (from Graph API Explorer)"
                        handleOnChange={handleChange}
                        className="workflow-api-field-full"
                    />
                    {!isViewMode && (
                        <Box className="workflow-api-connect-row">
                            <Button
                                className="edit-primary"
                                variant="outlined"
                                onClick={handleMetaConnect}
                            >
                                Connect to Meta
                            </Button>
                        </Box>
                    )}
                </>
            )}

            <StyledSubHeader>Step 2 : Select Page & Lead Form</StyledSubHeader>
            <InfoText>Select the Facebook page and choose the lead form.</InfoText>

            <SelectBoxWithLabel
                select
                name="page_id"
                disabled={isViewMode || pages.length === 0}
                required
                value={config.page_id || ''}
                label="Facebook Page"
                optionMenuItem={pages.map(p => ({ label: p.name, value: p.id }))}
                handleOnChange={handlePageSelect}
                className="workflow-api-field-full"
                error={!!validationErrors.page_id}
            />

            <SelectBoxWithLabel
                select
                name="form_id"
                disabled={isViewMode || forms.length === 0 || !config.page_id}
                required
                value={config.form_id || ''}
                label="Lead Gen Form"
                optionMenuItem={forms.map(f => ({ label: f.status ? `${f.name} (${f.status})` : f.name, value: f.id }))}
                handleOnChange={handleFormSelect}
                className="workflow-api-field-full"
                error={!!validationErrors.form_id}
            />
        </Stack>
    );

    const renderGenericForm = () => (
        <Stack className="workflow-api-form-stack">
            <StyledSubHeader>General Configuration</StyledSubHeader>
            <TextFiledWithLabel
                name="url"
                required
                disabled={isViewMode}
                value={config.url || ''}
                label="Endpoint URL"
                handleOnChange={handleChange}
                className="workflow-api-field-full"
                error={!!validationErrors.url}
            />
            <Stack className="workflow-api-form-row">
                <SelectBoxWithLabel
                    select name="method" disabled={isViewMode} required
                    value={config.method || WORKFLOW_HTTP_METHODS.GET} label="HTTP Method"
                    optionMenuItem={workflowHttpMethods} handleOnChange={handleChange}
                    className="workflow-api-field-48"
                />
                <SelectBoxWithLabel
                    select name="authType" disabled={isViewMode}
                    value={config.authType || WORKFLOW_AUTH_TYPES.NONE}
                    label="Authentication Type"
                    optionMenuItem={authTypeOptions} handleOnChange={handleChange}
                    className="workflow-api-field-48"
                />
            </Stack>

            {config.authType === WORKFLOW_AUTH_TYPES.BEARER && (
                <TextFiledWithLabel
                    name="authToken" required disabled={isViewMode}
                    value={config.authToken || ''} label="Bearer Token"
                    handleOnChange={handleChange} className="workflow-api-field-full"
                    error={!!validationErrors.authToken}
                />
            )}

            {config.authType === WORKFLOW_AUTH_TYPES.API_KEY && (
                <Stack className="workflow-api-form-row">
                    <SelectBoxWithLabel
                        select name="apiKeyLocation" disabled={isViewMode} required
                        value={config.apiKeyLocation || WORKFLOW_API_KEY_LOCATIONS.HEADER} label="API Key Location"
                        optionMenuItem={workflowApiKeyLocations} handleOnChange={handleChange}
                        className="workflow-api-field-30"
                    />
                    <TextFiledWithLabel
                        name="apiKeyName" required disabled={isViewMode}
                        value={config.apiKeyName || ''} label="Key Name"
                        handleOnChange={handleChange} className="workflow-api-field-30"
                        error={!!validationErrors.apiKeyName}
                    />
                    <TextFiledWithLabel
                        name="apiKeyValue" required disabled={isViewMode}
                        value={config.apiKeyValue || ''} label="Key Value"
                        handleOnChange={handleChange} className="workflow-api-field-30"
                        error={!!validationErrors.apiKeyValue}
                    />
                </Stack>
            )}
        </Stack>
    );

    // ── Main render ───────────────────────────────────────────────────────────
    return (
        <LeadDialog
            open={open}
            handleClose={handleCloseDialog}
            title={handleValidateCodevalue(internationalise, 'WF_TP_CRED_TITLE') || 'API Access Details'}
            size="md"
            btnlabel={!isViewMode ? (handleValidateCodevalue(internationalise, 'COMMON_SAVE') || 'Save') : undefined}
            handleSave={!isViewMode ? handleSaveClick : undefined}
        >
            <Box className="workflow-api-dialog-content">
                {/* Provider + Auth Type — always shown */}
                <Stack className="workflow-api-provider-row">
                    <SelectBoxWithLabel
                        select name="provider" disabled={isViewMode}
                        value={config.provider || ''}
                        label="3rd Party Provider"
                        optionMenuItem={workflowThirdPartyProviders}
                        handleOnChange={handleChange}
                        className="workflow-api-field-48"
                    />
                    <SelectBoxWithLabel
                        select name="authType" disabled={isViewMode}
                        value={config.authType || authTypeOptions[0]?.value || ''}
                        label="Authentication Type"
                        optionMenuItem={authTypeOptions}
                        handleOnChange={handleChange}
                        className="workflow-api-field-48"
                    />
                </Stack>

                {/* Meta wizard or generic form */}
                {isMetaOAuthConfig(config)
                    ? renderMetaWizard()
                    : providerConfig?.supportsGenericApiForm
                        ? renderGenericForm()
                        : null}
            </Box>
        </LeadDialog>
    );
};

export default ApiAccessDetailsDialog;
